import React, { useState, useEffect } from 'react'
import { RoleValidationService } from '../../lib/services/roleValidationService'

interface PermissionGuardProps {
  userId: string
  groupId?: string
  requiredPermission: 'superAdmin' | 'admin' | 'groupManagement' | 'curriculumManagement' | 'eventManagement' | 'contentDeletion' | 'analyticsAccess' | 'settingsManagement' | 'taskAssignment' | 'groupDataAccess'
  fallback?: React.ReactNode
  children: React.ReactNode
  loadingComponent?: React.ReactNode
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  userId,
  groupId,
  requiredPermission,
  fallback = <div>Access Denied</div>,
  children,
  loadingComponent = <div>Loading permissions...</div>
}) => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkPermission = async () => {
      if (!userId) {
        setHasPermission(false)
        setIsLoading(false)
        return
      }

      try {
        let result

        switch (requiredPermission) {
          case 'superAdmin':
            result = await RoleValidationService.validateSuperAdmin(userId)
            break
          case 'admin':
            result = await RoleValidationService.validateAdmin(userId)
            break
          case 'groupManagement':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateGroupManagement(userId, groupId)
            break
          case 'curriculumManagement':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateCurriculumManagement(userId, groupId)
            break
          case 'eventManagement':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateEventManagement(userId, groupId)
            break
          case 'contentDeletion':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateContentDeletion(userId, groupId)
            break
          case 'analyticsAccess':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateAnalyticsAccess(userId, groupId)
            break
          case 'settingsManagement':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateGroupSettingsManagement(userId, groupId)
            break
          case 'taskAssignment':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateTaskAssignment(userId, groupId)
            break
          case 'groupDataAccess':
            if (!groupId) {
              setHasPermission(false)
              setIsLoading(false)
              return
            }
            result = await RoleValidationService.validateGroupDataAccess(userId, groupId)
            break
          default:
            setHasPermission(false)
            setIsLoading(false)
            return
        }

        setHasPermission(result.isValid)
      } catch (error) {
        setHasPermission(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkPermission()
  }, [userId, groupId, requiredPermission])

  if (isLoading) {
    return <>{loadingComponent}</>
  }

  if (!hasPermission) {
    return <>{fallback}</>
  }

  return <>{children}</>
} 